/**
 * harness/copyHistory.js
 * T-Cron-2026-10-28 03:30 早间 (T118) — 改进清单 #1「复制体验」补齐:**最近复制历史**
 *
 * 为什么需要:
 *   看板的典型用法是**反复复制同一批选题的文案** —— 先复制 A 版正文去小红书
 *   粘贴,发现开头不够抓人就回来复制标题 + 标签拼一版完整的;或者 A 版复制完,
 *   临时想看 B 版的措辞,又切回去复制一次。
 *
 *   Vite 版(实际在用的源码树)只有「复制当前版」这一个入口,**没有任何地方
 *   告诉用户刚才复制过什么** ⇒ 用户想再拿一次上次的文案,只能:
 *     关抽屉 → 重新找到那条卡片 → 打开 → 切到那个版本 → 再点复制
 *   这条路径在真实工作流里几乎没人走完 —— 于是用户改成「复制到一半先留着
 *   剪贴板,怕被下一次复制冲掉」,最后干脆**不复制**,直接手打。
 *
 *   老仓库(根目录 components/PostDetail.jsx,老 Babel 分叉)有「复制历史」
 *   (T43 加的 `.detail-recent-copies`),Vite 重写时丢了 ⇒ 这是典型的
 *   「清单说完成 ≠ 线上可用」的**可达性缺口**,和 T104 的 #7、T107 的 #14
 *   同一形态。本轮把它按老仓库语义移植到 Vite 树。
 *
 * 存储口径(与老仓库逐字对齐,便于老用户迁移):
 *   · localStorage key = `xhs-kanban-recent-copies`(沿用老仓库的 key,
 *     老用户老数据直接可读,不会因为换树就丢历史);
 *   · 最多留 5 条(FIFO),老仓库 RECENT_COPIES_MAX = 5;
 *   · 去重:同一段文本再次复制 ⇒ 移到最前,而不是出现两条;
 *   · 每条记 { ts, kind, book, version, text, charCount }。
 *     `charCount` 在复制那一刻算好存下来(老仓库 T-Cron-2026-10-07 的做法):
 *     历史列表里直接显示「856 字」,用户不用再切回详情页去看字数。
 *
 * 异常处理(与 harness/searchHistory.js 同策略):
 *   localStorage 读/写任何异常(隐私模式 / 配额满 / JSON 损坏)**一律吞掉**。
 *   复制历史是**锦上添花**,读不出来绝不能让整个看板崩掉 ——
 *   剪贴板写失败是硬伤(用户要重打),历史读失败只是少了个便利功能。
 *
 * 注意:本文件是 .js(不是 .jsx)⇒ **必须显式 import React**
 * (vite.config.js 的 esbuild.jsxInject 只对 .jsx 生效)。
 */
import React from 'react';

const STORAGE_KEY = 'xhs-kanban-recent-copies';

/** 最多保留多少条复制历史 */
export const MAX_COPY_HISTORY = 5;

/**
 * 规范化一条历史记录。
 * 字段缺失/类型不对的条目直接丢掉 —— localStorage 是**用户可写**的
 * (浏览器 devtools 一改就是脏数据),不能让一条坏记录把列表渲染崩。
 */
function normalizeEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const text = typeof entry.text === 'string' ? entry.text : '';
  if (!text) return null;
  const charCount = Number(entry.charCount);
  return {
    ts: Number(entry.ts) || 0,
    kind: typeof entry.kind === 'string' ? entry.kind : 'body',
    book: typeof entry.book === 'string' ? entry.book : '',
    version: typeof entry.version === 'string' ? entry.version : '',
    text,
    charCount: Number.isFinite(charCount) && charCount > 0 ? Math.round(charCount) : 0,
  };
}

/**
 * 从 localStorage 读复制历史。任何异常一律返回空数组。
 */
export function loadCopyHistory() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return [];
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeEntry)
      .filter(Boolean)
      .slice(0, MAX_COPY_HISTORY);
  } catch (_) {
    return [];
  }
}

/**
 * 写回 localStorage(带去重 + 截断)。返回真正写下去的那一份。
 */
export function saveCopyHistory(list) {
  const clean = [];
  const seen = new Set();
  for (const item of list || []) {
    const entry = normalizeEntry(item);
    if (!entry || seen.has(entry.text)) continue;
    seen.add(entry.text);
    clean.push(entry);
    if (clean.length >= MAX_COPY_HISTORY) break;
  }
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
    }
  } catch (_) {
    // 写失败(配额满 / 隐私模式)不影响本次会话内的使用
  }
  return clean;
}

/**
 * 记一次复制:去重 + 移到最前 + 截断。
 * @param {{kind?:string, book?:string, version?:string, text:string, charCount?:number}} entry
 * @returns {Array} 新的历史列表
 */
export function pushCopyHistory(entry) {
  const e = normalizeEntry(entry);
  if (!e) return loadCopyHistory();
  const rest = loadCopyHistory().filter((x) => x.text !== e.text);
  return saveCopyHistory([e, ...rest]);
}

/** 清空全部复制历史 */
export function clearCopyHistory() {
  return saveCopyHistory([]);
}

/**
 * React 侧包装:状态 + 两个操作。
 *
 * 刻意**不做**跨标签页同步(同 searchHistory.js 的理由):一台设备一个人用,
 * 收益远小于复杂度。
 */
export function useCopyHistory() {
  const [history, setHistory] = React.useState(() => loadCopyHistory());

  const push = React.useCallback((entry) => {
    setHistory(pushCopyHistory(entry));
  }, []);

  const clear = React.useCallback(() => {
    setHistory(clearCopyHistory());
  }, []);

  return { history, push, clear };
}

const CopyHistory = {
  MAX_COPY_HISTORY,
  loadCopyHistory,
  saveCopyHistory,
  pushCopyHistory,
  clearCopyHistory,
  useCopyHistory,
};

if (typeof window !== 'undefined') window.CopyHistory = CopyHistory;

export default CopyHistory;
